import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { userInfoState, platformState } from "../global-state";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
} from "@mhome/ui";
import Developer from "./developer";
import About from "./about";
import Usage from "./usage";
import DesktopSoftwareUpdate from "./desktop-software-update";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";

const Setting = () => {
  const history = useHistory();
  const userInfo = useRecoilValue(userInfoState);
  const [selectedPage, setSelectedPage] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const location = useLocation();
  const [platform, setPlatform] = useRecoilState(platformState);
  const isSmallScreen = useIsSmallScreen();

  const isApp = platform.source === "app";
  const isDesktop = platform.source === "desktop";

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    if (location.hash) {
      setSelectedPage(location.hash.substring(1));
    } else if (!isSmallScreen && !location.hash) {
      const firstVisibleRoute = routes.find(
        (page) => page.hash !== "developer" || userInfo?.setting?.engAccount
      );
      if (firstVisibleRoute) {
        setSelectedPage(firstVisibleRoute.hash);
        history.replace(`/setting#${firstVisibleRoute.hash}`);
      }
    }
    setIsDefault(!location.hash);
  }, [location.hash, isSmallScreen, userInfo?.setting?.engAccount]);

  const routes = [
    ...(!isApp && !isDesktop
      ? [{ hash: "developer", title: "Developer", component: Developer }]
      : []),

    { hash: "usage", title: "Subscription & Usage", component: Usage },

    ...(isDesktop
      ? [
          {
            hash: "software-update",
            title: "Software Update",
            component: DesktopSoftwareUpdate,
          },
        ]
      : []),
    { hash: "about", title: "About", component: About },
  ];

  const handleNavigation = (hash) => {
    setIsDefault(false);
    setSelectedPage(hash);
    if (isSmallScreen) {
      history.replace(`/setting#${hash}`);
    } else {
      history.replace(`/setting#${hash}`);
    }
  };

  const handleUserLogout = async () => {
    history.push("/user-logout");
  };

  return (
    <div
      className="flex flex-col overflow-hidden h-full"
      style={{
        padding: isSmallScreen ? "0" : "16px",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        backgroundColor: "transparent",
      }}
    >
      <style>{`
        .setting-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="setting-container flex justify-between h-full items-start gap-4 relative">
        {/* Sidebar */}
        {(!isSmallScreen || (isSmallScreen && isDefault)) && (
          <div
            className="setting-container flex-shrink-0 rounded-2xl overflow-y-auto"
            style={{
              width: isSmallScreen ? "100%" : "35%",
              maxWidth: isSmallScreen ? "100%" : "375px",
              minHeight: "80vh",
              height: "100%",
              position: isSmallScreen ? "relative" : "sticky",
              top: 0,
              padding: "16px",
              backgroundColor: "transparent",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div className="flex flex-col items-center mb-4 p-4 gap-2">
              <Avatar
                style={{
                  width: 80,
                  height: 80,
                  marginTop: "8px",
                  backgroundColor: "hsl(var(--primary))" || "#9e9e9e",
                  fontSize: "2rem",
                  fontWeight: 600,
                  boxShadow: "none",
                  color: document.documentElement.classList.contains("dark")
                    ? "#333333"
                    : "#f5f5f5",
                }}
              >
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}
              </Avatar>
              <Typography
                variant="h6"
                style={{
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                {userInfo.name}
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              {routes
                .filter((page) => {
                  return (
                    page.hash !== "developer" || userInfo?.setting?.engAccount
                  );
                })
                .map((page, index) => (
                  <Card
                    key={index}
                    className="rounded-2xl"
                    style={{
                      backgroundColor:
                        !isSmallScreen && selectedPage === page.hash
                          ? "hsl(var(--accent))"
                          : getComponentBgColor("hsl(var(--card))"),
                      boxShadow: "none",
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleNavigation(page.hash)}
                      className="transition-colors"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "hsl(var(--accent))";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <CardContent
                        style={{
                          textAlign: "center",
                          paddingTop: "16px",
                          paddingBottom: "16px",
                        }}
                      >
                        <Typography variant="body1">{page.title}</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              <Card
                className="rounded-2xl"
                style={{
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  boxShadow: "none",
                }}
              >
                <CardActionArea
                  onClick={() => {
                    handleUserLogout();
                  }}
                  className="transition-colors"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 59, 48, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <CardContent
                    style={{
                      textAlign: "center",
                      paddingTop: "16px",
                      paddingBottom: "16px",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{
                        color: "#FF3B30",
                        fontWeight: 600,
                      }}
                    >
                      Sign out
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </div>
          </div>
        )}

        {/* Content */}
        {(!isSmallScreen || (isSmallScreen && !isDefault)) && (
          <div
            className="setting-container flex-grow h-full overflow-y-auto"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              minHeight: isSmallScreen ? "100vh" : "80vh",
            }}
          >
            <div style={{ height: "100%" }}>
              {selectedPage === "developer" &&
                userInfo?.setting?.engAccount && <Developer />}

              {selectedPage === "usage" && <Usage />}

              {selectedPage === "software-update" && <DesktopSoftwareUpdate />}
              {selectedPage === "about" && <About />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Setting;
